<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { creditNoteQueries, fetchInvoiceQueryOptions } from '@/features/invoices/queries'
import InvoiceSkeleton from '@/features/invoices/components/partials/InvoiceSkeleton.vue'
import InvoiceSkeletonInvoiceDetails from '@/features/invoices/components/partials/InvoiceSkeletonInvoiceDetails.vue'
import PageContent from '@/components/page-content.vue'
import InvoiceSkeletonBankingDetails from '@/features/invoices/components/partials/InvoiceSkeletonBankingDetails.vue'
import InvoiceSkeletonSummary from '@/features/invoices/components/partials/InvoiceSkeletonSummary.vue'
import { computed, reactive, ref, useTemplateRef } from 'vue'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import InvoiceSkeletonBillTotal from '@/features/invoices/components/partials/InvoiceSkeletonBillTotal.vue'
import InvoiceSkeletonCharges from '@/features/invoices/components/partials/InvoiceSkeletonCharges.vue'
import InvoiceSkeletonMeterReadings from '@/features/invoices/components/partials/InvoiceSkeletonMeterReadings.vue'
import InvoiceSkeletonCustomerDetails from '@/features/invoices/components/partials/InvoiceSkeletonCustomerDetails.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { faArrowLeft, faFloppyDisk, faHandPointUp, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { useUpdateInvoice, useUpdateInvoiceCharges, useUpdateInvoiceCategories } from '@/features/invoices/mutations'
import useLoader from '@/composables/loader'
import useNotifications from '@/composables/notifications'
import { ChargeType, InvoiceStatusNumeric } from '@/features/invoices/constants'
import { useRouter } from 'vue-router'
import { useUpdateInvoiceMeterReadings } from '@/composables/billing/invoices'
import ButtonSecondary from '@/components/button-secondary.vue'
import ButtonHighlighted from '@/components/button-highlighted.vue'
import ModalEscalationWarning from '@/views/billing/invoicing/modal-escalation-warning.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import ModalConfirmApproveAndSend from '@/views/billing/invoicing/modal-confirm-approve-and-send.vue'
import { useModalState } from '@/shared/composables/useModalState'
import InvoiceSkeletonCreditNotes from '@/features/invoices/components/partials/InvoiceSkeletonCreditNotes.vue'
import { watchUntil } from '@/shared/composables/watch-until'
import { useReplaceCreditNoteAllocations } from '@/features/invoices/composables/use-replace-credit-note-allocations'
import { useApproveInvoice } from '@/features/invoices/mutations/invoices'
import type { ComponentProps } from '@/types'
import InvoiceApprovalAffectedByCreditNoteErrorsModal
  from '@/views/billing/invoicing/InvoiceApprovalAffectedByCreditNoteErrorsModal.vue'
import {
  getFailedCreditNotesForInvoiceApprovalWarning
} from '@/features/invoices/lib/getFailedCreditNotesForInvoiceApprovalWarning'
import { ApiError } from '@/shared/api/validation'
import { shouldShowApproveInvoiceWarningModal } from '@/features/invoices/lib/shouldShowApproveInvoiceWarningModal'
import { assetQueries } from '@/features/assets/queries'
import { AssetType } from '@/features/assets/constants'
import InvoiceSkeletonSummaryFullWidth
  from '@/features/invoices/components/partials/InvoiceSkeletonSummaryFullWidth.vue'

const props = defineProps<{ invoiceId: number }>()
const { data: invoice, isError, error, isFetching } = useQuery(fetchInvoiceQueryOptions(props.invoiceId))
const { data: assetDetails } = useQuery(assetQueries.details(() => invoice.value?.assetCode))

const router = useRouter()
const { wrap } = useLoader()
const { addSuccess, addError } = useNotifications()
const { open: showApproveAndSendConfirmation, isOpen: isConfirmingApproveAndSend, close: hideApproveAndSendConfirmation } = useModalState()
const { mutateAsync: approveInvoiceAsync } = useApproveInvoice()
const { mutateAsync: updateInvoiceAsync } = useUpdateInvoice(props.invoiceId)
const { mutateAsync: updateCategoriesAsync } = useUpdateInvoiceCategories(props.invoiceId)
const { mutateAsync: updateChargesAsync } = useUpdateInvoiceCharges(props.invoiceId)
const { mutateAsync: updateMeterReadingsAsync } = useUpdateInvoiceMeterReadings(props.invoiceId)
const { mutateAsync: updateCreditNoteAllocationsAsync } = useReplaceCreditNoteAllocations(props.invoiceId)
const chargesComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonCharges>>('charges')
const metersComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonMeterReadings>>('meters')
const creditNotesComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonCreditNotes>>('creditNotes')
const showSavings = ref(true)
const poNumber = ref('')
const showMeters = ref(true)
const currentCreditNoteAllocationState = ref<{ error: boolean, amountInCents: number }>()
const escalationWarning = ref<string>()
const flattenedCharges = computed(() => invoice.value?.categories.flatMap(x => x.charges) ?? [])

// Update some values when the invoice loads initially.
watchUntil(
  invoice,
  newValue => {
    poNumber.value = newValue.poNumber
    showSavings.value = newValue.showSavings
    showMeters.value = newValue.showMeterReadings
  }
)

const invoiceAmounts = computed(
  () => {
    const totalCreditedAmountInCents = currentCreditNoteAllocationState.value?.amountInCents ?? (invoice.value?.totalCreditedInRands ?? 0)
    const amountDueInCents = (
      currentCreditNoteAllocationState.value === undefined
        ? (invoice.value?.totalAmountDueInRands ?? 0) * 100
        : (invoice.value?.totalAmountIncludingTax ?? 0) * 100 - currentCreditNoteAllocationState.value.amountInCents
    )

    return {
      totalCreditedAmountInCents,
      amountDueInCents,
    }
  }
)
const approvalWarningModal = reactive(useModalState<ComponentProps<typeof InvoiceApprovalAffectedByCreditNoteErrorsModal>>())
const navigateToInvoiceDashboard = () => router.push({ name: 'invoices.list' })

function approveInvoice() {
  return wrap(
    () => approveInvoiceAsync(props.invoiceId)
  ).then(
    () => {
      addSuccess({ title: 'Invoice approved successfully.' })
    },
    (err: unknown) => {
      if (err instanceof ApiError) {
        const escalationTaskError = err.errors.find(e => e.pointer?.toLowerCase() == 'escalationid')

        if (escalationTaskError) {
          escalationWarning.value = escalationTaskError.message
          return
        }
      }

      if (shouldShowApproveInvoiceWarningModal(err)) {
        approvalWarningModal.open({
          creditNoteNames: getFailedCreditNotesForInvoiceApprovalWarning(err),
          invoiceId: props.invoiceId,
          projectCode: invoice.value!.projectCode,
          assetCode: invoice.value!.assetCode,
          projectName: invoice.value!.projectName
        })
      }

      addError({ title: 'Invoice could not be approved.' })
    }
  )
}

async function persistChanges() {
  const statusBeforeSave = invoice.value?.invoiceStatusId

  const changedMeters = metersComponent.value?.changes() ?? []
  const changedCharges = chargesComponent.value?.changes().charges ?? []
  const changedCategories = chargesComponent.value?.changes().categories ?? []
  const creditNoteAllocations = creditNotesComponent.value?.allocations() ?? []

  await wrap(
    async () => {
      const promises = [
        updateInvoiceAsync({
          showSavings: showSavings.value,
          poNumber: poNumber.value,
          showMeterReadings: showMeters.value,
        }),
        updateCreditNoteAllocationsAsync(
          creditNoteAllocations.map(
            x => ({ creditNoteItemId: x.id, allocatedAmountInCents: x.amountInCents })
          )
        )
      ]

      if (changedCategories.length > 0) {
        promises.push(
          updateCategoriesAsync(
            changedCategories.map(
              x => ({
                ...x.values,
                id: x.id,
              })
            )
          )
        )
      }

      if (changedCharges.length > 0) {
        promises.push(
          updateChargesAsync(
            changedCharges.map(
              x => ({
                ...(flattenedCharges.value.find(y => y.id === x.id) ?? {}),
                ...x.values,
                id: x.id,
              })
            )
          )
        )
      }

      if (changedMeters.length > 0) {
        promises.push(
          updateMeterReadingsAsync(
            changedMeters.map(
              changed => {
                const existing = invoice.value!.meterReadings.find(meter => meter.id === changed.id)!

                return {
                  ...existing,
                  ...changed.values,
                  id: changed.id,
                }
              }
            )
          )
        )
      }

      await Promise.all(promises)

      if (statusBeforeSave !== InvoiceStatusNumeric.Draft) {
        await navigateToInvoiceDashboard()
      }
    }
  ).then(
    () => {
      addSuccess({ title: 'Invoice updated successfully' })

      metersComponent.value?.clearChanges()
      chargesComponent.value?.clearChanges()
    },
    (e) => {
      addError({ title: 'Unable to update invoice', message: 'Please try again' })
    }
  )
}
</script>

<template>
  <PageContent>
    <ModalEscalationWarning v-if="escalationWarning !== undefined" :message="escalationWarning" @close="escalationWarning = undefined" />
    <ModalConfirmApproveAndSend v-if="isConfirmingApproveAndSend"
                                @close="hideApproveAndSendConfirmation"
                                :invoice-id="invoiceId" />

    <ContentLoader v-if="isFetching" :loading="isFetching" />
    <HeroSplashError v-else-if="isError" />
    <InvoiceSkeleton v-else-if="invoice" title="Tax Invoice">
      <template #footer >
        <template v-if="![InvoiceStatusNumeric.Approved, InvoiceStatusNumeric.Sent].includes(invoice.invoiceStatusId)">
          <ButtonPrimary :disabled="currentCreditNoteAllocationState?.error" @click="persistChanges()" :icon="faFloppyDisk">Save</ButtonPrimary>
          <ButtonSecondary :icon="faXmark" @click="navigateToInvoiceDashboard">Cancel</ButtonSecondary>
          <template v-if="invoice.invoiceStatusId === InvoiceStatusNumeric.Draft">
            <div class="vr"></div>
            <ButtonHighlighted :disabled="currentCreditNoteAllocationState?.error" :icon="faHandPointUp" @click="approveInvoice">Approve</ButtonHighlighted>
            <ButtonSecondary :disabled="currentCreditNoteAllocationState?.error" outline="teal" :icon="faHandPointUp" @click="showApproveAndSendConfirmation">Approve & Send</ButtonSecondary>
          </template>
        </template>
        <template v-else>
          <ButtonSecondary :icon="faArrowLeft" @click="navigateToInvoiceDashboard">Back to invoicing dashboard</ButtonSecondary>
        </template>
      </template>

      <template #charges>
        <InvoiceSkeletonCharges ref="charges"
                                :editable-charge-types="[InvoiceStatusNumeric.Draft, InvoiceStatusNumeric.ActionRequired].includes(invoice.invoiceStatusId) ? [ChargeType.Usage, ChargeType.Recurring] : []"
                                :total-excluding-tax="invoice.totalAmountExcludingTax"
                                :total-including-tax="invoiceAmounts.amountDueInCents / 100"
                                :tax="invoice.tax"
                                :tax-percentage="invoice.taxPercentage"
                                :categories="invoice.categories"
                                :asset-code="invoice.assetCode"
                                :summary-type="assetDetails?.type !== AssetType.Wheeling ? 'all' : 'category'" />
      </template>

      <template #meters v-if="assetDetails?.type !== AssetType.Wheeling">
        <InvoiceSkeletonMeterReadings ref="meters"
                                      :invoice-id="invoiceId"
                                      :invoice-status="invoice.invoiceStatusId"
                                      :exported="invoice.isExportPower"
                                      v-model:show="showMeters"
                                      :grid-consumption="invoice.totalGridConsumption"
                                      :solar-consumption="invoice.totalEnergyConsumption"
                                      :exported-energy="invoice.totalEnergyExported"
                                      :meters="invoice.meterReadings" />
      </template>

      <template #customer-details>
        <InvoiceSkeletonCustomerDetails :name="invoice.customer.name"
                                        :address="[
                                          invoice.customer.addressLineOne,
                                          invoice.customer.addressLineTwo,
                                          invoice.customer.suburb,
                                          invoice.customer.city,
                                          invoice.customer.region,
                                          invoice.customer.country,
                                        ]"
                                        :vat-number="invoice.customer.vatNumber"
                                        v-model:po-number="poNumber" />
      </template>

      <template #bill-total>
        <InvoiceSkeletonBillTotal :amount="invoiceAmounts.amountDueInCents / 100"
                                  :due-date="invoice.dueDate" />
      </template>

      <template #owner-details>
        <InvoiceSkeletonOwnerDetails :name="invoice.contractFundEntity.name"
                                     :address="invoice.contractFundEntity.address"
                                     :logo-url="invoice.contractFundEntity.logoUrl"
                                     :vat-number="invoice.contractFundEntity.vatNumber" />
      </template>

      <template #invoice-details>
        <InvoiceSkeletonInvoiceDetails :invoice-number="invoice.invoiceNumber"
                                       :date="invoice.invoiceDate"
                                       :period="[invoice.periodStartDate, invoice.periodEndDate]"
                                       :date-format="invoice.invoiceDateFormat" />
      </template>
      <template #banking-details>
        <InvoiceSkeletonBankingDetails :account-holder="invoice.contractFundEntity.accountName"
                                       :bank="invoice.contractFundEntity.bank"
                                       :account-number="invoice.contractFundEntity.accountNumber"
                                       :branch-code="invoice.contractFundEntity.branchCode" />
      </template>

      <template #summary v-if="assetDetails?.type !== AssetType.Wheeling">
        <InvoiceSkeletonSummary v-model:show="showSavings"
                                :savings-to-date="invoice.totalSavingsToDate"
                                :savings="invoice.invoiceSavings?.amount ?? 0"
                                :amount-exc-tax="invoice.totalAmountExcludingTax" />
      </template>

      <template #summary-full-width v-else>
        <InvoiceSkeletonSummaryFullWidth :amount-excluding-tax-in-cents="invoice.totalAmountExcludingTax * 100"
                                         :credit-amount-in-cents="invoiceAmounts.totalCreditedAmountInCents"
                                         :tax-percentage="invoice.taxPercentage"
                                         :tax-amount-in-cents="invoice.tax * 100"
                                         :amount-due-in-cents="invoiceAmounts.amountDueInCents" />
      </template>

      <template #credit-notes v-if="assetDetails?.type === AssetType.Wheeling">
        <InvoiceSkeletonCreditNotes ref="creditNotes"
                                    :invoice-id="invoice.id"
                                    :invoice-status="invoice.invoiceStatusId"
                                    @update:allocated="currentCreditNoteAllocationState = $event" />
      </template>
    </InvoiceSkeleton>
  </PageContent>

  <InvoiceApprovalAffectedByCreditNoteErrorsModal v-if="approvalWarningModal.isOpen"
                                            v-bind="approvalWarningModal.state"
                                            @close="approvalWarningModal.close" />
</template>

<style scoped lang="scss">

</style>
